import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { ChipComponent } from '../chip/chip.component';
import { CommonModule } from '@angular/common';
import { ChipListItem } from './interfaces';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

type ComponentSize = 'default' | 'large';

@Component({
  selector: 'crm-chip-list',
  templateUrl: './chip-list.component.html',
  styleUrls: ['./chip-list.component.scss'],
  imports: [ChipComponent, CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipListComponent<T> {
  @Input({ transform: coerceBooleanProperty }) hideLabelWhenSelected = false;

  @Input({ required: true }) items: ChipListItem<T>[] = [];

  @Input() selected?: T;
  @Output() selectedChange = new EventEmitter<T>();

  @Input() size: ComponentSize = 'default';

  @HostListener('focusin') onFocusIn(): void {
    this.captureFocus = true;
    this.focusedIndex = this.items?.findIndex((item) => item.value === this.selected) ?? -1;
  }
  @HostListener('focusout') onFocusOut(): void {
    this.captureFocus = false;
    this.focusedIndex = -1;
  }

  protected focusedIndex = -1;
  protected captureFocus = false;

  protected handleChipClick(value: T): void {
    this.setSelected(value);
  }

  protected handleKeyDown(event: KeyboardEvent): void {
    switch (event.code) {
      case 'ArrowLeft':
        if (--this.focusedIndex < 0) {
          this.focusedIndex = this.items.length - 1;
        }
        break;
      case 'ArrowRight':
        if (++this.focusedIndex >= this.items.length) {
          this.focusedIndex = 0;
        }
        break;
      case 'Enter':
      case 'Space':
        this.setSelected(this.items?.[this.focusedIndex]?.value);
        if (event.target instanceof HTMLElement) {
          if (event.target?.nextElementSibling instanceof HTMLElement) {
            event.target.nextElementSibling.focus();
          }
        }
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  protected setSelected(value: T): void {
    if (this.selected === value) {
      return;
    }
    this.selected = value;
    this.selectedChange.emit(value);
  }
}
